/**
 * Shared OrderDesk folder + folder-order loader for Printing Toolkit.
 * Mirrors PackersToolkit URL contract:
 *   GET /folders
 *   GET /orders?folder={id|name}&limit=500&includeOrders=1[&dateField=delivery|dispatch]
 *
 * Does not import PackersToolkit. Packers may adopt this later.
 */

import { parseDDMMYYYY, formatYYYYMMDD, formatDDMMYYYY } from './dateService.js';
import { isOrderDeskOrderSheetHeaderFirstCell } from '../utils/orderDeskOrderSheetHeaders.js';
import {
  extractOrderDeskLineSku,
  extractOrderDeskLineQty,
} from '../utils/orderDeskLineItemFields.js';

export const ORDERDESKER_BASE =
  process.env.REACT_APP_ORDERDESKER_URL ||
  'https://orderdesker-844411985132.europe-west2.run.app';

const DATE_FOLDER_RE = /^\d{2}-\d{2}-\d{4}$/;

export function isOrderDeskDateFolderName(name) {
  return DATE_FOLDER_RE.test(String(name || '').trim());
}

/**
 * Convert a date-folder name (DD-MM-YYYY) to YYYY-MM-DD for print counts / session.
 * @param {string} folderName
 * @returns {string}
 */
export function orderDeskFolderNameToIsoDay(folderName) {
  const name = String(folderName || '').trim();
  if (!isOrderDeskDateFolderName(name)) return '';
  const parsed = parseDDMMYYYY(name);
  return parsed ? formatYYYYMMDD(parsed) : '';
}

/**
 * OrderDesk folder order URL.
 *
 * Packers / Printing / Production (`scope: 'membership'`, default for fetch):
 * always uses folder **id** so the list matches OrderDesk folder contents.
 *
 * Legacy date-match (`scope: 'dateField'`): date folder + delivery|dispatch uses folder
 * **name** + `dateField` (orders whose Delivery/Dispatch equals that day).
 *
 * Named folders always use membership (id).
 *
 * @param {{ id?: string|number, name?: string }|null} folder
 * @param {'delivery'|'dispatch'} [dateField='delivery']
 * @param {{ scope?: 'dateField'|'membership' }} [options]
 * @returns {{ folderParam: string, url: string, isDateFolder: boolean, scope: string }}
 */
export function buildOrderDeskFolderOrdersRequest(
  folder,
  dateField = 'delivery',
  options = {},
) {
  if (!folder) {
    return { folderParam: '', url: '', isDateFolder: false, scope: 'membership' };
  }
  const isDateFolder = isOrderDeskDateFolderName(folder.name);
  const scope =
    options.scope === 'dateField'
      ? 'dateField'
      : 'membership';
  const field = dateField === 'dispatch' ? 'dispatch' : 'delivery';
  const useDateField =
    scope === 'dateField' &&
    isDateFolder &&
    (field === 'dispatch' || field === 'delivery');
  const folderParam = useDateField
    ? String(folder.name)
    : String(folder.id != null ? folder.id : folder.name || '');
  const base = ORDERDESKER_BASE.replace(/\/$/, '');
  let url = `${base}/orders?folder=${encodeURIComponent(folderParam)}&limit=500&includeOrders=1`;
  if (useDateField) url += `&dateField=${field}`;
  return {
    folderParam,
    url,
    isDateFolder,
    scope: useDateField ? 'dateField' : 'membership',
  };
}

export function sortOrderDeskFolders(folders) {
  return [...(folders || [])].sort((a, b) => {
    const aIsDate = isOrderDeskDateFolderName(a?.name);
    const bIsDate = isOrderDeskDateFolderName(b?.name);
    if (aIsDate && bIsDate) {
      const dateA = parseDDMMYYYY(a.name) || new Date(a.name);
      const dateB = parseDDMMYYYY(b.name) || new Date(b.name);
      return dateB - dateA;
    }
    if (aIsDate && !bIsDate) return -1;
    if (!aIsDate && bIsDate) return 1;
    return String(a?.name || '').localeCompare(String(b?.name || ''));
  });
}

/**
 * Packers auto-select: next Friday → nearest Friday → Wholesale → closest date → first.
 * @param {Array<{id:*, name:string}>} folders
 * @param {Date} [now]
 * @returns {{id:*, name:string}|null}
 */
export function findAutoSelectOrderDeskFolder(folders, now = new Date()) {
  if (!folders || folders.length === 0) return null;

  const today = new Date(now);
  today.setHours(12, 0, 0, 0);
  const currentDay = today.getDay();
  const daysUntilFriday = currentDay <= 5 ? 5 - currentDay : 5 + (7 - currentDay);
  const nextFriday = new Date(today);
  nextFriday.setDate(today.getDate() + daysUntilFriday);
  const nextFridayStr = formatDDMMYYYY(nextFriday);

  const exactFridayMatch = folders.find((folder) => folder.name === nextFridayStr);
  if (exactFridayMatch) return exactFridayMatch;

  const fridayFolders = folders.filter((folder) => {
    if (!isOrderDeskDateFolderName(folder.name)) return false;
    const folderDate = parseDDMMYYYY(folder.name);
    return folderDate && folderDate.getDay() === 5;
  });
  if (fridayFolders.length > 0) {
    fridayFolders.sort((a, b) => {
      const dateA = parseDDMMYYYY(a.name);
      const dateB = parseDDMMYYYY(b.name);
      const distA = Math.abs(dateA - today);
      const distB = Math.abs(dateB - today);
      if (distA !== distB) return distA - distB;
      const aUpcoming = dateA >= today ? 0 : 1;
      const bUpcoming = dateB >= today ? 0 : 1;
      if (aUpcoming !== bUpcoming) return aUpcoming - bUpcoming;
      return dateA - dateB;
    });
    return fridayFolders[0];
  }

  const wholesaleFolder = folders.find((folder) =>
    String(folder.name || '')
      .toLowerCase()
      .includes('wholesale'),
  );
  if (wholesaleFolder) return wholesaleFolder;

  const dateFolders = folders.filter((folder) =>
    isOrderDeskDateFolderName(folder.name),
  );
  if (dateFolders.length > 0) {
    dateFolders.sort((a, b) => {
      const dateA = parseDDMMYYYY(a.name);
      const dateB = parseDDMMYYYY(b.name);
      const distA = Math.abs(dateA - today);
      const distB = Math.abs(dateB - today);
      if (distA !== distB) return distA - distB;
      const aUpcoming = dateA >= today ? 0 : 1;
      const bUpcoming = dateB >= today ? 0 : 1;
      if (aUpcoming !== bUpcoming) return aUpcoming - bUpcoming;
      return dateA - dateB;
    });
    return dateFolders[0];
  }

  return folders[0];
}

async function fetchJson(url) {
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const err = new Error(`OrderDesk request failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  return response.json();
}

/**
 * @returns {Promise<Array<{id:*, name:string}>>}
 */
export async function fetchOrderDeskFolders() {
  const base = ORDERDESKER_BASE.replace(/\/$/, '');
  const data = await fetchJson(`${base}/folders`);
  return sortOrderDeskFolders(data.folders || []);
}

function toIsoOrRaw(value) {
  if (!value) return null;
  const parsed = parseDDMMYYYY(String(value).trim());
  if (parsed) return formatYYYYMMDD(parsed);
  const normalized = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) return normalized.slice(0, 10);
  return normalized || null;
}

function mergeItems(existingItems, detailItems) {
  const items = Array.isArray(existingItems) ? [...existingItems] : [];
  if (!Array.isArray(detailItems) || !detailItems.length) return items;
  const sums = new Map();
  const names = new Map();
  detailItems.forEach((item) => {
    if (!item) return;
    const sku = extractOrderDeskLineSku(item);
    if (!sku) return;
    const qty = extractOrderDeskLineQty(item);
    if (qty <= 0) return;
    sums.set(sku, (sums.get(sku) || 0) + qty);
    if (item.name) names.set(sku, item.name);
  });
  sums.forEach((totalQty, sku) => {
    const existing = items.find((ex) => String(ex.sku || '').trim() === sku);
    if (existing) {
      existing.quantity = Math.max(Number(existing.quantity) || 0, totalQty);
      if (!existing.name && names.get(sku)) existing.name = names.get(sku);
    } else {
      items.push({
        sku,
        quantity: totalQty,
        name: names.get(sku) || null,
      });
    }
  });
  return items;
}

/**
 * Normalize OrderDesk folder payload into print-ready order rows.
 * Stable `id` prefers source id (Packers/Firestore display key), then OD id.
 * @param {object} payload
 * @param {{ id?:*, name?: string }|null} folder
 * @returns {object[]}
 */
export function normalizeOrderDeskFolderOrders(payload, folder = null) {
  const data = payload && typeof payload === 'object' ? payload : {};
  const rawOrders = data.values || data.orders || [];

  const orderDetailsMap = new Map();
  const orderDetailsBySourceId = new Map();
  const registerDetail = (orderObj) => {
    if (!orderObj) return;
    const id = orderObj.id || orderObj.order_id || orderObj.orderId;
    if (id != null) orderDetailsMap.set(String(id), orderObj);
    if (orderObj.source_id) {
      orderDetailsBySourceId.set(String(orderObj.source_id).trim(), orderObj);
    }
    if (orderObj.order_number) {
      orderDetailsBySourceId.set(String(orderObj.order_number).trim(), orderObj);
    }
    const cd = orderObj.checkout_data || orderObj.checkoutData || {};
    const sourceIdFromCheckout = cd['Source ID#'] || cd['Order Number'];
    if (sourceIdFromCheckout) {
      orderDetailsBySourceId.set(String(sourceIdFromCheckout).trim(), orderObj);
    }
  };

  if (Array.isArray(data.orders)) data.orders.forEach(registerDetail);
  if (Array.isArray(data.orderDetails)) data.orderDetails.forEach(registerDetail);
  if (data.orderMap && typeof data.orderMap === 'object') {
    Object.values(data.orderMap).forEach(registerDetail);
  }

  const orderMap = new Map();

  rawOrders.forEach((row) => {
    let orderId;
    let rowData;
    if (Array.isArray(row)) {
      const rawOrderId = row[0];
      if (!rawOrderId) return;
      orderId = String(rawOrderId).trim();
      if (!orderId || isOrderDeskOrderSheetHeaderFirstCell(orderId)) return;
      rowData = row;
    } else if (row && typeof row === 'object') {
      orderId = String(row.source_id || row.order_number || row.id || '').trim();
      if (!orderId) return;
      rowData = null;
    } else {
      return;
    }

    const detail =
      rowData === null
        ? row
        : orderDetailsMap.get(orderId) || orderDetailsBySourceId.get(orderId);
    const checkoutData = detail?.checkout_data || detail?.checkoutData || {};
    const detailDispatchRaw =
      checkoutData.Dispatch ||
      checkoutData.dispatch ||
      checkoutData.dispatch_date ||
      checkoutData['Dispatch Date'];
    const detailDeliveryRaw =
      checkoutData.Delivery ||
      checkoutData.delivery ||
      checkoutData.delivery_date ||
      checkoutData['Delivery Date'];

    if (!orderMap.has(orderId)) {
      const csvCustomerCompany = rowData ? rowData[3] : null;
      const csvShippingMethod = rowData ? rowData[14] : null;
      const csvDispatchDate = rowData ? rowData[15] : null;
      const csvDeliveryDate = rowData ? rowData[16] : null;
      const csvNotes = rowData ? rowData[17] : null;

      const sourceId = String(
        detail?.source_id ||
          checkoutData['Source ID#'] ||
          checkoutData['Order Number'] ||
          detail?.order_number ||
          orderId,
      ).trim();

      const listedId = sourceId || String(detail?.id || orderId);
      const shippingMethod =
        detail?.shipping_method ||
        detail?.shipping?.method ||
        csvShippingMethod ||
        '';

      const entry = {
        ...detail,
        orderId,
        orderNumber: sourceId,
        sourceId,
        source_id: sourceId,
        id: listedId,
        order_id: detail?.id || detail?.order_id || orderId,
        customerCompany:
          detail?.customer?.company || csvCustomerCompany || '',
        companyName:
          detail?.customer?.company ||
          detail?.shipping?.company ||
          csvCustomerCompany ||
          '',
        shippingMethod,
        shipping_method: shippingMethod,
        checkout_data: {
          ...checkoutData,
          ...(detailDispatchRaw || csvDispatchDate
            ? { Dispatch: detailDispatchRaw || csvDispatchDate }
            : {}),
          ...(detailDeliveryRaw || csvDeliveryDate
            ? { Delivery: detailDeliveryRaw || csvDeliveryDate }
            : {}),
        },
        checkoutData: undefined,
        dispatch_date: detailDispatchRaw || csvDispatchDate || null,
        delivery_date: detailDeliveryRaw || csvDeliveryDate || null,
        dispatchDate: toIsoOrRaw(detailDispatchRaw || csvDispatchDate),
        deliveryDate: toIsoOrRaw(
          detailDeliveryRaw || csvDeliveryDate || folder?.name,
        ),
        notes: detail?.order_notes?.[0]?.content || csvNotes || '',
        order_notes: Array.isArray(detail?.order_notes)
          ? detail.order_notes
          : [],
        items: [],
        order_items: [],
        folderId: folder?.id ?? null,
        folderName: folder?.name ?? null,
        _detailMerged: false,
      };
      delete entry.checkoutData;
      orderMap.set(orderId, entry);
    }

    const orderEntry = orderMap.get(orderId);
    if (!orderEntry) return;

    if (rowData && rowData[12]) {
      const sheetSku = String(rowData[12] || '').trim();
      const sheetQty = parseFloat(rowData[13]) || 1;
      if (sheetSku) {
        const existing = orderEntry.items.find(
          (i) => String(i.sku || '').trim() === sheetSku,
        );
        if (existing) {
          existing.quantity = Math.max(Number(existing.quantity) || 0, sheetQty);
        } else {
          orderEntry.items.push({
            sku: sheetSku,
            quantity: sheetQty,
            name: null,
          });
        }
      }
    }

    const detailItems = detail?.order_items;
    if (Array.isArray(detailItems) && detailItems.length && !orderEntry._detailMerged) {
      orderEntry.items = mergeItems(orderEntry.items, detailItems);
      orderEntry._detailMerged = true;
    }
    orderEntry.order_items = orderEntry.items;
  });

  return Array.from(orderMap.values()).map((order) => {
    const { _detailMerged, ...rest } = order;
    rest.order_items = Array.isArray(rest.items) ? rest.items : [];
    return rest;
  });
}

/**
 * @param {{
 *   folder: {id:*, name:string},
 *   dateField?: 'delivery'|'dispatch',
 *   scope?: 'dateField'|'membership',
 * }} args
 * @returns {Promise<object[]>}
 */
export async function fetchOrderDeskFolderOrders({
  folder,
  dateField = 'delivery',
  scope = 'membership',
} = {}) {
  if (!folder) return [];
  const { url } = buildOrderDeskFolderOrdersRequest(folder, dateField, { scope });
  if (!url) return [];
  const payload = await fetchJson(url);
  return normalizeOrderDeskFolderOrders(payload, folder);
}
